import React, { useState, useEffect } from 'react';

import pdfImage from 'assets/images/pdf.png';
import folderImage from 'assets/images/folder.png';
import docImage from 'assets/images/doc.png';
import pptImage from 'assets/images/ppt.svg';
import videoImage from 'assets/images/video.png';
import textImage from 'assets/images/text.png';
import xlsImage from 'assets/images/xls.png';
import fileImage from 'assets/images/file.png';
import { getDisplayableImageUrl, isHeicFile } from 'utils/heicUtils';
import { getSignedUrl } from 'modules/FileManagerV2/api/services';

function Thumbnail({ item }) {
  const [displayUrl, setDisplayUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fileName = item.display_name || item.file_name;

  useEffect(() => {
    const fileType = returnFileType(getFileExtension(fileName));

    if (fileType !== 'image') {
      setDisplayUrl(null);
      return undefined;
    }

    let cancelled = false;

    const loadThumbnail = async () => {
      setIsLoading(true);
      try {
        let resolvedUrl = item.url;
        if (!resolvedUrl && item.file_path) {
          resolvedUrl = await getSignedUrl({ file_path: item.file_path });
        }

        if (!resolvedUrl || cancelled) return;

        if (isHeicFile(fileName)) {
          const convertedUrl = await getDisplayableImageUrl(resolvedUrl, fileName, 0.3);
          if (!cancelled) setDisplayUrl(convertedUrl);
        } else if (!cancelled) {
          setDisplayUrl(resolvedUrl);
        }
      } catch (error) {
        console.error('Failed to load image thumbnail:', error);
        if (!cancelled && item.url) setDisplayUrl(item.url);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadThumbnail();

    return () => {
      cancelled = true;
    };
  }, [item.url, item.file_path, fileName]);

  const returnFileType = (format) => {
    switch (format.toLowerCase()) {
      case 'pdf':
        return 'pdf';
      case 'docx':
      case 'doc':
        return 'doc';
      case 'ppt':
      case 'pptx':
        return 'ppt';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
      case 'bmp':
      case 'heic':
      case 'heif':
      case 'svg':
      case 'ico':
      case 'jfif':
        return 'image';
      case 'mp4':
      case 'avi':
      case 'mkv':
      case 'mov':
      case 'ogg':
        return 'video';
      case 'txt':
        return 'text';
      case 'xlsx':
      case 'xls':
        return 'xls';
      default:
        return 'folder';
    }
  };

  function getFileExtension(fileName) {
    if (fileName) {
      const lastIndex = fileName?.lastIndexOf('.');
      if (lastIndex === -1) return '';
      return fileName.substring(lastIndex + 1);
    }
    return '';
  }

  const getThumbnailSrc = () => {
    if (item.entry_type === 'folder') {
      return folderImage;
    }

    const fileType = returnFileType(getFileExtension(fileName));

    switch (fileType) {
      case 'image':
        if (isLoading || !displayUrl) {
          return fileImage;
        }
        return displayUrl;
      case 'doc':
        return docImage;
      case 'ppt':
        return pptImage;
      case 'video':
        return videoImage;
      case 'pdf':
        return pdfImage;
      case 'text':
        return textImage;
      case 'xls':
        return xlsImage;
      default:
        return fileImage;
    }
  };

  return (
    <img
      src={getThumbnailSrc()}
      alt={item.isFolder ? 'Folder' : 'File'}
      style={{
        width: '64px',
        height: '64px',
        margin: 'auto',
        borderRadius: '16px 16px 0px 0px',
        objectFit: 'cover',
        ...(returnFileType(getFileExtension(fileName)) === 'image' ? { width: '100%', height: '75%', padding: '8px' } : {}),
      }}
    />
  );
}

export default Thumbnail;
